import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import { useOpenSourceDocument } from '@ctw/shared/content/CCDA/use-open-source-document';
import { RiskGapAddToNoteButton } from '@ctw/shared/content/risk/risk-gap-add-to-note-button';
import { RiskGapCopyButton } from '@ctw/shared/content/risk/risk-gap-copy-button';
import { isRiskGapNoteEditable } from '@ctw/shared/content/risk/utils';
import { useRiskGapSummaryNoteContext } from '@ctw/shared/context/controlled-text-context';
import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { type ZusFhirResource, summarizeFhirResource } from '@zeus-health/types';
import { useMemo } from 'react';
import { ClinicalEvidenceCard } from './clinical-evidence-card';
import {
  EVIDENCE_SECTION_ORDER,
  type EvidenceSectionKey,
  RESOURCE_TYPE_TO_SECTION,
  useEvidenceResources,
} from './use-evidence-resources';

function getSectionForResourceType(resourceType: string): EvidenceSectionKey | 'Other' {
  return RESOURCE_TYPE_TO_SECTION[resourceType] ?? 'Other';
}

export const RiskGapEvidence = ({ riskGap }: { riskGap: RiskGapModel }) => {
  const { setControlledText: setNote } = useRiskGapSummaryNoteContext();
  const evidence = useEvidenceResources(riskGap);
  const { evidenceItems, isLoading, hasEvidence } = evidence;

  const telemetry = useTelemetry();
  const { openResourceDrawer } = useResourceDrawer();
  const { openSourceDocument } = useOpenSourceDocument();

  const summaries = useMemo(
    () =>
      evidenceItems.map(({ resource, quote }) => {
        const summary = summarizeFhirResource(resource.resource as ZusFhirResource);
        const text = quote ? `${quote} — ${summary}` : summary;
        return { id: resource.id, resourceType: resource.resourceType, text };
      }),
    [evidenceItems],
  );

  const groupedBySection = useMemo(() => {
    const order: Array<EvidenceSectionKey | 'Other'> = [...EVIDENCE_SECTION_ORDER, 'Other'];
    const groups = new Map<
      EvidenceSectionKey | 'Other',
      Array<{ id: string; resourceType: string; text: string }>
    >();
    for (const key of order) {
      groups.set(key, []);
    }
    for (const { id, resourceType, text } of summaries) {
      const section = getSectionForResourceType(resourceType);
      groups.get(section)?.push({ id, resourceType, text });
    }
    return order
      .map((section) => ({ section, items: groups.get(section) ?? [] }))
      .filter(({ items }) => items.length > 0);
  }, [summaries]);

  const copyContent = useMemo(
    () =>
      groupedBySection
        .map(
          ({ section, items }) => `${section}:\n${items.map(({ text }) => `- ${text}`).join('\n')}`,
        )
        .join('\n\n'),
    [groupedBySection],
  );

  const isNoteEditable = isRiskGapNoteEditable(riskGap.status);

  if (!hasEvidence) {
    return <div className={tw`text-content-subtle text-sm`}>No clinical evidence available.</div>;
  }

  if (isLoading) {
    return <div className={tw`text-content-subtle text-sm`}>Loading evidence…</div>;
  }

  return (
    <div className={tw`space-y-4`}>
      {summaries.length > 0 && (
        <div
          className={tw`group overflow-hidden rounded-lg border border-border bg-white shadow-sm`}
        >
          <div
            className={tw`flex h-14 items-center justify-between border-border border-b bg-muted/30 p-4`}
          >
            <span
              className={tw`font-semibold text-meta-main text-muted-foreground text-xs uppercase tracking-wider`}
            >
              Evidence Summary
            </span>
            <div className={tw`flex-shrink flex-nowrap font-semibold text-muted-foreground`}>
              {isNoteEditable ? (
                <RiskGapAddToNoteButton textToAdd={copyContent} addTextCallback={setNote} />
              ) : null}
              <RiskGapCopyButton
                tooltipContent="Copy"
                tooltipId="evidence-summary-copy"
                buttonAriaLabel="Copy evidence summary to clipboard"
                buttonDisabled={!copyContent}
                copyContent={copyContent}
              />
            </div>
          </div>
          <div className={tw`p-4`}>
            <div className={tw`whitespace-pre-wrap text-sm`}>{copyContent}</div>
          </div>
        </div>
      )}

      <div
        className={twx('grid grid-cols-1 gap-4 lg:grid-cols-2', {
          'lg:grid-cols-1': evidenceItems.length === 1,
        })}
      >
        {evidenceItems.map(({ resource, quote }) => (
          <ClinicalEvidenceCard
            key={`${resource.resourceType}-${resource.id}`}
            resource={resource}
            quote={quote}
            showAddToNoteButton={isNoteEditable}
            onClick={() => {
              if (resource.resourceType === 'DocumentReference') {
                void openSourceDocument(resource);
              } else {
                openResourceDrawer({ telemetry, model: resource });
              }
            }}
          />
        ))}
      </div>
    </div>
  );
};
